// src/components/Settings/Layout/ParentalControl.js

import React, { useState, useEffect } from "react";
import "../Styles/ParentalControl.css"

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const ALLOWED_KID_PROFILES = [
  { value: "sports",  label: "🏅 Sports",  icon: "🏅" },
  { value: "dance",   label: "💃 Dance",   icon: "💃" },
  { value: "music",   label: "🎵 Music",   icon: "🎵" },
  { value: "drama",   label: "🎭 Drama",   icon: "🎭" },
  { value: "speaker", label: "🎤 Speaker", icon: "🎤" },
];

const CONTENT_FILTERS = [
  { key: "blockSexual",    icon: "🔞", label: "Sexual Content",     desc: "Block adult, explicit, or suggestive content" },
  { key: "blockViolence",  icon: "⚔️", label: "Violent Content",    desc: "Block graphic violence, gore, or combat" },
  { key: "blockPolitical", icon: "🏛️", label: "Political Content",  desc: "Block political debates and propaganda" },
  { key: "blockReligious", icon: "🛐", label: "Religious Content",  desc: "Block religious discussions and content" },
  { key: "blockGambling",  icon: "🎰", label: "Gambling Content",   desc: "Block gambling, betting, and related topics" },
  { key: "blockDrugs",     icon: "💊", label: "Drug-Related Content", desc: "Block content related to drugs and substances" },
];

const PERMISSION_DEFS = [
  { key: "canDeleteProfile",  icon: "🗑️", label: "Delete Profiles",   desc: "Allow child to delete their own profiles" },
  { key: "canArchiveProfile", icon: "📦", label: "Archive Profiles",  desc: "Allow child to archive posts and profiles" },
  { key: "canMessage",        icon: "💬", label: "Direct Messaging",  desc: "Allow child to send and receive messages" },
  { key: "canAudioCall",      icon: "📞", label: "Audio Calls",       desc: "Allow child to make and receive audio calls" },
  { key: "canAddFriends",     icon: "👥", label: "Add Friends",       desc: "Allow child to send/accept friend requests" },
  { key: "canJoinGroups",     icon: "🏘️", label: "Join Groups",       desc: "Allow child to join or create groups" },
  { key: "canCreateProfiles", icon: "➕", label: "Create Profiles",   desc: "Allow child to create new activity profiles" },
  { key: "canSubscribe",      icon: "💳", label: "Paid Subscriptions", desc: "Allow child to purchase paid subscriptions (disabled for safety)" },
];

const FIELD_META = {
  name:     { label: "Full Name",    type: "text",     placeholder: "Child's full name",      icon: "👤" },
  email:    { label: "Email",        type: "email",    placeholder: "child@example.com",       icon: "✉️" },
  userId:   { label: "User ID",      type: "text",     placeholder: "Unique login username",   icon: "🆔" },
  mobile:   { label: "Mobile",       type: "tel",      placeholder: "+1 234 567 8900",         icon: "📱" },
  password: { label: "Password",     type: "password", placeholder: "Min. 8 characters",       icon: "🔑" },
  dob:      { label: "Date of Birth", type: "date",    placeholder: "",                        icon: "🎂" },
};

const DEFAULT_CHILD_INFO = { name: "", email: "", userId: "", mobile: "", password: "", dob: "" };
const DEFAULT_PERMISSIONS = {
  canDeleteProfile: false, canArchiveProfile: false,
  canMessage: false,       canAudioCall: false,
  canAddFriends: false,    canJoinGroups: false,
  canCreateProfiles: false, canSubscribe: false,
};
const DEFAULT_CONTENT_FILTERS = {
  blockSexual: true, blockViolence: true, blockPolitical: false,
  blockReligious: false, blockGambling: true, blockDrugs: true,
};

// ─────────────────────────────────────────────────────────────────────────────
// SHARED PRIMITIVES
// ─────────────────────────────────────────────────────────────────────────────

/** Vertical form row: label + icon + hint + input */
const PCFormRow = ({ fieldKey, value, onChange }) => {
  const meta = FIELD_META[fieldKey];
  return (
    <div className="pc-form-row">
      <label className="pc-form-label" htmlFor={`pc-${fieldKey}`}>
        {meta.label}
      </label>
      <input
        id={`pc-${fieldKey}`}
        type={meta.type}
        placeholder={meta.placeholder}
        value={value}
        onChange={e => onChange(fieldKey, e.target.value)}
        className="pc-input"
        autoComplete="off"
        required
      />
    </div>
  );
};

/** Toggle row with icon, label, description */
const PCToggleRow = ({ icon, label, desc, checked, onChange, disabled = false }) => (
  <div className={`pc-toggle-row ${disabled ? "pc-toggle-row--disabled" : ""}`}>
    <span className="pc-toggle-icon">{icon}</span>
    <div className="pc-toggle-info">
      <span className="pc-toggle-label">{label}</span>
      <span className="pc-toggle-desc">{desc}</span>
    </div>
    <label className={`st-switch ${disabled ? "pc-switch--disabled" : ""}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <span className={`st-slider ${disabled ? "" : checked ? "" : ""}`} />
    </label>
  </div>
);

/** Section header bar */
const PCSection = ({ icon, title, subtitle, children, accent }) => (
  <div className="pc-section">
    <div className="pc-section-header">
      <span className="pc-section-icon">{icon}</span>
      <div>
        <h3 className="pc-section-title">{title}</h3>
        {subtitle && <p className="pc-section-subtitle">{subtitle}</p>}
      </div>
    </div>
    <div className="pc-section-body">{children}</div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// PARENTAL CONTROL PANEL
// ─────────────────────────────────────────────────────────────────────────────

const ParentalControl = () => {
  const [isChildMode,      setIsChildMode]      = useState(false);
  const [childInfo,        setChildInfo]        = useState(DEFAULT_CHILD_INFO);
  const [formComplete,     setFormComplete]     = useState(false);
  const [childProfiles,    setChildProfiles]    = useState([]);
  const [permissions,      setPermissions]      = useState(DEFAULT_PERMISSIONS);
  const [contentFilters,   setContentFilters]   = useState(DEFAULT_CONTENT_FILTERS);
  const [friendFilterMode, setFriendFilterMode] = useState("restricted");
  const [groupJoinMode,    setGroupJoinMode]    = useState("approval");
  const [maxProfiles,      setMaxProfiles]      = useState(3);
  const [toast,            setToast]            = useState(null);
  const [createdAccounts,  setCreatedAccounts]  = useState([]);
  const [activeTab,        setActiveTab]        = useState("info");

  // ── Validation ────────────────────────────────────────────────────────
  useEffect(() => {
    const filled = Object.values(childInfo).every(v => v.trim() !== "");
    setFormComplete(filled);
  }, [childInfo]);

  // ── Toast helper ──────────────────────────────────────────────────────
  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ── Handlers ──────────────────────────────────────────────────────────
  const handleInfoChange = (field, value) =>
    setChildInfo(prev => ({ ...prev, [field]: value }));

  const handlePermissionToggle = (key) => {
    if (key === "canSubscribe") return;
    setPermissions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleContentFilterToggle = (key) =>
    setContentFilters(prev => ({ ...prev, [key]: !prev[key] }));

  const handleProfileAdd = (profileValue) => {
    if (!profileValue) return;
    if (childProfiles.includes(profileValue)) {
      showToast("This profile is already added.", "warning");
      return;
    }
    if (childProfiles.length >= maxProfiles) {
      showToast(`Maximum ${maxProfiles} profiles allowed.`, "error");
      return;
    }
    setChildProfiles(prev => [...prev, profileValue]);
  };

  const handleProfileRemove = (profileValue) =>
    setChildProfiles(prev => prev.filter(p => p !== profileValue));

  const handleReset = () => {
    setChildInfo(DEFAULT_CHILD_INFO);
    setChildProfiles([]);
    setPermissions(DEFAULT_PERMISSIONS);
    setContentFilters(DEFAULT_CONTENT_FILTERS);
    setFriendFilterMode("restricted");
    setGroupJoinMode("approval");
    setMaxProfiles(3);
    setActiveTab("info");
    showToast("Form has been reset.", "warning");
  };

  const handleSubmit = () => {
    if (!formComplete) {
      showToast("Please fill in all child information fields.", "error");
      return;
    }

    const newChildAccount = {
      accountId:    `child-${Date.now()}`,
      linkedParent: "current-parent-userid",
      ...childInfo,
      profiles:      childProfiles,
      permissions:   { ...permissions, canSubscribe: false },
      contentFilters,
      socialControls: { friendFilterMode, groupJoinMode },
      maxProfiles,
      createdAt:     new Date().toISOString(),
    };

    const existing = JSON.parse(localStorage.getItem("childAccounts") || "[]");
    localStorage.setItem("childAccounts", JSON.stringify([...existing, newChildAccount]));

    setCreatedAccounts(prev => [...prev, newChildAccount]);
    showToast(`✅ Child account for "${childInfo.name}" created! They can log in with their User ID and password.`, "success");

    setChildInfo(DEFAULT_CHILD_INFO);
    setChildProfiles([]);
    setActiveTab("info");
  };

  const PC_TABS = [
    { key: "info",       label: "👤 Child Info"       },
    { key: "profiles",   label: "🎭 Profiles"         },
    { key: "social",     label: "👥 Social Controls"  },
    { key: "content",    label: "🛡 Content Filters"  },
    { key: "perms",      label: "⚙️ Permissions"      },
    { key: "accounts",   label: `📋 Accounts (${createdAccounts.length})` },
  ];

  return (
    <div className="st-panel pc-root">
      <h2 className="st-panel-title">👨‍👩‍👧 Parental Control</h2>
      <p className="st-panel-subtitle">
        Create and manage child accounts. All child accounts are automatically linked to your parent account —
        no separate signup required.
      </p>

      {toast && (
        <div className={`pc-toast pc-toast--${toast.type}`}>
          <span>{toast.message}</span>
          <button className="pc-toast-close" onClick={() => setToast(null)}>×</button>
        </div>
      )}

      <div className="pc-master-toggle">
        <div className="pc-master-toggle-info">
          <span className="pc-master-toggle-title">
            {isChildMode ? " Child Account Mode Active" : "Manage a Child Account"}
          </span>
          <span className="pc-master-toggle-desc">
            {isChildMode
              ? "Fill in the tabs below to configure and create the child account."
              : "Enable to create a new linked child account under your parent account."}
          </span>
        </div>
        <label className="st-switch">
          <input
            type="checkbox"
            checked={isChildMode}
            onChange={e => {
              setIsChildMode(e.target.checked);
              if (!e.target.checked) handleReset();
            }}
          />
          <span className="st-slider" />
        </label>
      </div>

      {isChildMode && (
        <>
          <div className="pc-inner-tabs">
            {PC_TABS.map(t => (
              <button
                key={t.key}
                className={`pc-inner-tab ${activeTab === t.key ? "pc-inner-tab--active" : ""}`}
                onClick={() => setActiveTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="pc-tab-content">
            {/* TAB 1: CHILD INFORMATION */}
            {activeTab === "info" && (
              <PCSection title="Child Account Information"
                subtitle="This account will be automatically linked to your parent account. No separate signup is required."
              >
                <div className="pc-info-banner" style={{background: "none", border: "1px solid gray"}}>
                  <p>
                    Once created, the child account is <strong>automatically linked</strong> to your
                    parent account. The child can log in directly using the <strong>User ID</strong> and
                    <strong> password</strong> you set below.
                  </p>
                </div>
                <div className="pc-form-grid">
                  {Object.keys(DEFAULT_CHILD_INFO).map(field => (
                    <PCFormRow
                      key={field}
                      fieldKey={field}
                      value={childInfo[field]}
                      onChange={handleInfoChange}
                    />
                  ))}
                </div>
                {formComplete && (
                  <div className="pc-form-complete-banner">
                    ✅ All fields complete — proceed to the Profiles tab.
                  </div>
                )}
              </PCSection>
            )}

            {/* TAB 2: PROFILES */}
            {activeTab === "profiles" && (
              <PCSection icon="🎭" title="Child Profiles"
                subtitle="Add allowed activity profiles for this child. Maximum profiles can be set below."
                accent="rgba(156,39,176,0.50)"
              >
                <div className="pc-max-profiles-row">
                  <span className="pc-form-label">Maximum Profiles Allowed</span>
                  <div className="pc-stepper">
                    <button
                      type="button"
                      className="pc-stepper-btn"
                      onClick={() => setMaxProfiles(p => Math.max(1, p - 1))}
                      disabled={maxProfiles <= 1}
                    >−</button>
                    <span className="pc-stepper-val">{maxProfiles}</span>
                    <button
                      type="button"
                      className="pc-stepper-btn"
                      onClick={() => setMaxProfiles(p => Math.min(5, p + 1))}
                      disabled={maxProfiles >= 5}
                    >+</button>
                  </div>
                </div>

                <div className="pc-profile-selector">
                  <p className="pc-form-label" style={{ marginBottom: 12 }}>
                    Add Profile Type <span className="pc-badge">{childProfiles.length}/{maxProfiles}</span>
                  </p>
                  <div className="pc-profile-chips">
                    {ALLOWED_KID_PROFILES.map(p => {
                      const added      = childProfiles.includes(p.value);
                      const maxReached = childProfiles.length >= maxProfiles;
                      return (
                        <button
                          key={p.value}
                          type="button"
                          className={`pc-profile-chip ${added ? "pc-profile-chip--added" : ""} ${!added && maxReached ? "pc-profile-chip--disabled" : ""}`}
                          onClick={() => added ? handleProfileRemove(p.value) : handleProfileAdd(p.value)}
                          disabled={!added && maxReached}
                        >
                          {p.icon} {p.value.charAt(0).toUpperCase() + p.value.slice(1)}
                          {added && <span className="pc-chip-remove">×</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {childProfiles.length > 0 && (
                  <div className="pc-selected-profiles">
                    <p className="pc-form-label" style={{ marginBottom: 10 }}>Selected Profiles</p>
                    <div className="pc-selected-profiles-list">
                      {childProfiles.map((p, i) => {
                        const meta = ALLOWED_KID_PROFILES.find(ap => ap.value === p);
                        return (
                          <div key={i} className="pc-selected-profile-tag">
                            <span>{meta?.icon}</span>
                            <span>{p.charAt(0).toUpperCase() + p.slice(1)}</span>
                            <button
                              type="button"
                              className="pc-remove-profile-btn"
                              onClick={() => handleProfileRemove(p)}
                            >×</button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="pc-info-banner" style={{ marginTop: 16 }}>
                  <span>ℹ️</span>
                  <p>Child can create profiles only from the list above. Profile creation can be further restricted in the Permissions tab.</p>
                </div>
              </PCSection>
            )}

            {/* TAB 3: SOCIAL CONTROLS */}
            {activeTab === "social" && (
              <>
                <PCSection icon="👥" title="Friends Selection Control"
                  subtitle="Control how the child can add or interact with friends."
                  accent="rgba(0,188,212,0.50)"
                >
                  <div className="pc-radio-group">
                    {[
                      { val: "open",       label: "Open",       desc: "Child can freely add anyone as a friend.",                 icon: "🌐" },
                      { val: "restricted", label: "Restricted", desc: "Child can request friends, but parent must approve.",      icon: "🔍" },
                      { val: "none",       label: "Blocked",    desc: "Child cannot add friends or send friend requests at all.", icon: "🚫" },
                    ].map(opt => (
                      <label
                        key={opt.val}
                        className={`pc-radio-card ${friendFilterMode === opt.val ? "pc-radio-card--selected" : ""}`}
                        onClick={() => setFriendFilterMode(opt.val)}
                      >
                        <span className="pc-radio-card-icon">{opt.icon}</span>
                        <div className="pc-radio-card-info">
                          <span className="pc-radio-card-label">{opt.label}</span>
                          <span className="pc-radio-card-desc">{opt.desc}</span>
                        </div>
                        <span
                          className="pc-radio-dot"
                          style={{ background: friendFilterMode === opt.val ? "#4caf50" : "transparent",
                                   borderColor: friendFilterMode === opt.val ? "#4caf50" : "rgba(255,255,255,0.22)" }}
                        />
                      </label>
                    ))}
                  </div>
                </PCSection>

                <PCSection icon="🏘️" title="Group Join Control"
                  subtitle="Control whether and how the child can join groups."
                  accent="rgba(255,152,0,0.50)"
                >
                  <div className="pc-radio-group">
                    {[
                      { val: "open",     label: "Open",              desc: "Child can join any group freely.",                       icon: "🌐" },
                      { val: "approval", label: "Requires Approval",  desc: "Child can request to join, but parent approves first.",  icon: "✅" },
                      { val: "none",     label: "No Groups",          desc: "Child cannot join or view any groups.",                  icon: "🚫" },
                    ].map(opt => (
                      <label
                        key={opt.val}
                        className={`pc-radio-card ${groupJoinMode === opt.val ? "pc-radio-card--selected" : ""}`}
                        onClick={() => setGroupJoinMode(opt.val)}
                      >
                        <span className="pc-radio-card-icon">{opt.icon}</span>
                        <div className="pc-radio-card-info">
                          <span className="pc-radio-card-label">{opt.label}</span>
                          <span className="pc-radio-card-desc">{opt.desc}</span>
                        </div>
                        <span
                          className="pc-radio-dot"
                          style={{ background: groupJoinMode === opt.val ? "#4caf50" : "transparent",
                                   borderColor: groupJoinMode === opt.val ? "#4caf50" : "rgba(255,255,255,0.22)" }}
                        />
                      </label>
                    ))}
                  </div>
                </PCSection>
              </>
            )}

            {/* TAB 4: CONTENT FILTERS */}
            {activeTab === "content" && (
              <PCSection icon="🛡" title="Content Filtering Rules"
                subtitle="Choose which content categories are blocked for this child account. Blocked content will not appear in their feed, search, or recommendations."
                accent="rgba(244,67,54,0.50)"
              >
                <div className="pc-filter-grid">
                  {CONTENT_FILTERS.map(f => (
                    <PCToggleRow
                      key={f.key}
                      icon={f.icon}
                      label={f.label}
                      desc={f.desc}
                      checked={contentFilters[f.key]}
                      onChange={() => handleContentFilterToggle(f.key)}
                    />
                  ))}
                </div>
                <div className="pc-info-banner" style={{ marginTop: 20 }}>
                  <span>🛡</span>
                  <p>
                    Enabled filters are enforced at the platform level. The child cannot bypass or disable
                    these filters from their own account.
                  </p>
                </div>
              </PCSection>
            )}

            {/* TAB 5: PERMISSIONS */}
            {activeTab === "perms" && (
              <PCSection icon="⚙️" title="Child Account Permissions"
                subtitle="Fine-grained control over what actions the child can take on the platform."
                accent="rgba(255,193,7,0.50)"
              >
                <div className="pc-perms-grid">
                  {PERMISSION_DEFS.map(p => {
                    const isForced = p.key === "canSubscribe";
                    return (
                      <PCToggleRow
                        key={p.key}
                        icon={p.icon}
                        label={p.label}
                        desc={isForced ? "🔒 Subscriptions are always disabled for child accounts." : p.desc}
                        checked={isForced ? false : permissions[p.key]}
                        onChange={() => handlePermissionToggle(p.key)}
                        disabled={isForced}
                      />
                    );
                  })}
                </div>
                <div className="pc-subscription-warning">
                  <span className="pc-sub-warn-icon">💳</span>
                  <div>
                    <p className="pc-sub-warn-title">Subscriptions Always Blocked</p>
                    <p className="pc-sub-warn-desc">
                      Child accounts can never purchase paid subscriptions or make in-app purchases.
                      This is enforced at the account level and cannot be overridden by the child.
                    </p>
                  </div>
                </div>
              </PCSection>
            )}

            {/* TAB 6: CREATED ACCOUNTS */}
            {activeTab === "accounts" && (
              <PCSection icon="📋" title="Created Child Accounts"
                subtitle="All child accounts linked to your parent account. They log in with their User ID and password."
                accent="rgba(76,175,80,0.50)"
              >
                {createdAccounts.length === 0 ? (
                  <div className="pc-empty-accounts">
                    <span style={{ fontSize: "3rem" }}>👶</span>
                    <p>No child accounts created yet.</p>
                    <p style={{ fontSize: 12, color: "rgba(255,255,255,0.35)" }}>
                      Fill in the Child Info tab and submit to create one.
                    </p>
                  </div>
                ) : (
                  <div className="pc-accounts-list">
                    {createdAccounts.map((acc) => (
                      <div key={acc.accountId} className="pc-account-card">
                        <div className="pc-account-card-header">
                          <span className="pc-account-avatar">👶</span>
                          <div className="pc-account-card-info">
                            <span className="pc-account-name">{acc.name}</span>
                            <span className="pc-account-uid">@{acc.userId}</span>
                          </div>
                          <span className="pc-account-linked-badge">🔗 Linked</span>
                        </div>
                        <div className="pc-account-card-details">
                          <div className="pc-account-detail-row">
                            <span className="pc-detail-key">📧 Email</span>
                            <span className="pc-detail-val">{acc.email}</span>
                          </div>
                          <div className="pc-account-detail-row">
                            <span className="pc-detail-key">🎭 Profiles</span>
                            <span className="pc-detail-val">
                              {acc.profiles.length > 0 ? acc.profiles.join(", ") : "None"}
                            </span>
                          </div>
                          <div className="pc-account-detail-row">
                            <span className="pc-detail-key">👥 Friends</span>
                            <span className="pc-detail-val">
                              {acc.socialControls.friendFilterMode}
                            </span>
                          </div>
                          <div className="pc-account-detail-row">
                            <span className="pc-detail-key">💳 Subscriptions</span>
                            <span className="pc-detail-val" style={{ color: "#ef9a9a" }}>Blocked</span>
                          </div>
                          <div className="pc-account-detail-row">
                            <span className="pc-detail-key">📅 Created</span>
                            <span className="pc-detail-val">
                              {new Date(acc.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="pc-account-login-hint">
                          <span>🔐</span>
                          <span>Child logs in with User ID: <strong>{acc.userId}</strong></span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </PCSection>
            )}
          </div>

          {activeTab !== "accounts" && (
            <div className="pc-action-bar">
              <button type="button" className="pc-reset-btn" onClick={handleReset} style={{height: 50, width:200, marginTop: 5}}>
                Reset
              </button>
              <button
                type="button"
                className="st-save-btn pc-submit-btn"
                onClick={handleSubmit}
                disabled={!formComplete}
                style={{ opacity: formComplete ? 1 : 0.40, cursor: formComplete ? "pointer" : "not-allowed", color: "white" }}
              >
                Create Child Account
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ParentalControl;
